import { getApiEndpoint } from './config';
import { getWebTransport } from './transport';

/**
 * signed-request — 서명된 muzly 백엔드 호출 하나를 보내는 최소 단위.
 *
 * `profile.ts` 가 `/profile/0/mine` 전용으로 갖고 있던 실패 해석을 경로에 매이지 않게 꺼낸
 * 것이다. 진단(analy) 제출은 **상태코드로 재시도 여부를 가르므로**(5xx 재시도·4xx 폐기·
 * 409 종료) 실패가 상태코드를 실은 채 올라와야 한다. axios 예외와 lemon-core 의 봉투
 * 실패(200 + `{ error }`)를 한 모양으로 맞추는 자리가 여기다.
 */

/** 상태코드를 실은 실패. `status` 가 없으면 응답 자체를 못 받은 것(네트워크·타임아웃)이다. */
export interface HttpError extends Error {
  status?: number;
}

export const httpError = (
  message: string,
  status: number | undefined,
): HttpError => Object.assign(new Error(message), { status });

/** axios 실패에서 상태코드만 꺼낸다. 상태가 없으면 네트워크·타임아웃이다. */
export const statusOf = (error: unknown): number | undefined => {
  const status = (error as { response?: { status?: unknown } } | null)?.response
    ?.status;
  return typeof status === 'number' ? status : undefined;
};

/**
 * lemon-core 는 실패를 200 + `{ error: '403 NOT ALLOWED' }` 봉투로 낼 수도 있다.
 * 앞의 세 자리를 꺼내 axios 가 던진 실패와 같은 모양으로 맞춘다 — 그래야 재시도 판정이
 * 경로를 하나만 본다.
 */
export const envelopeStatus = (message: string): number | undefined => {
  const [, code] = /^([1-5]\d{2})\b/.exec(message) ?? [];
  return code === undefined ? undefined : Number(code);
};

export interface SignedRequestInit {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  /** API base 아래 경로. 앞에 `/` 를 붙인다 (`/analy/0`). */
  path: string;
  /** 있으면 body 로 실린다. `POST` 는 빈 객체라도 보내야 한다(프레임워크가 400 을 던진다). */
  body?: object;
  /**
   * 쿼리스트링. **`path` 에 `?` 로 붙이면 안 된다** — lemon-web-core 는 서명(SigV4)의
   * canonical query 를 이 값에서만 만들고 `path` 는 통째로 경로로 읽어, `?` 가 섞이면
   * 서명이 어긋나 `403` 이 온다(2026-09-07 `muz-d1` 실측: baseURL 쿼리 403, params 200).
   */
  params?: Record<string, string | number>;
}

/**
 * 서명 요청 하나. 실패는 **항상** `HttpError` 로 던진다 — 호출부가 `status` 하나만 보면 된다.
 * 세션이 아직 안 섰으면 서버가 `403` 으로 끊으므로, 기다림은 호출부가 정한다
 * (`waitForDeviceSession`).
 */
export const sendSignedRequest = async <T>(
  init: SignedRequestInit,
): Promise<T> => {
  try {
    const request = getWebTransport().buildSignedRequest({
      method: init.method,
      baseURL: `${getApiEndpoint()}${init.path}`,
    });
    const withParams =
      init.params === undefined ? request : request.setParams(init.params);
    const { data } = await (
      init.body === undefined ? withParams : withParams.setBody(init.body)
    ).execute<T & { error?: string }>();
    if (data?.error) {
      throw httpError(data.error, envelopeStatus(data.error));
    }
    return (data ?? {}) as T;
  } catch (error) {
    if (error instanceof Error && 'status' in error) {
      throw error;
    }
    throw httpError(
      error instanceof Error ? error.message : String(error),
      statusOf(error),
    );
  }
};
